// Zustand und Speichern des Kontakt-Formulars im Sheet (Handoff Sonstige Kontakte §3).
// Felder, Payload und Speicherweg wie bisher in KontaktErstellenFormular/KontaktEditFormular; Pflichtfelder inline.
import { useState } from 'react';
import { supabase } from '../supabase/client';
import { baueKontaktPayload, pflichtfeldFehler, tabAusKontakt, type KontaktFormFelder, type KontaktPflichtfeld, type KontaktTab } from './formular';
import type { SonstigerKontaktZeile } from './useSonstigerKontakt';

export type KontaktFeld = Exclude<keyof KontaktFormFelder, 'activeTab'>;

const s = (v: string | null | undefined) => v ?? '';

/** Startwerte: leer (Neu) bzw. aus der kontakte-Zeile (Bearbeiten) – wie in den bisherigen Formularen. */
export function startFelder(k: SonstigerKontaktZeile | null): KontaktFormFelder {
  return {
    activeTab: k ? tabAusKontakt(k) : 'privatperson',
    anrede: s(k?.anrede), vorname: s(k?.vorname), nachname: s(k?.nachname), kontaktart: s(k?.kontaktart), geburtsdatum: s(k?.geburtsdatum),
    unternehmensart: s(k?.unternehmensart), firmenname: s(k?.firma_name),
    strasse: s(k?.strasse), hausnummer: s(k?.hausnummer), plz: s(k?.plz), stadt: s(k?.stadt), land: s(k?.land),
    email: s(k?.email), festnetz: s(k?.festnetz), handynummer: s(k?.handynummer),
    kontoinhaber: s(k?.kontoinhaber), bank: s(k?.bank), iban: s(k?.iban), bic: s(k?.bic),
    steuernummer: s(k?.steuernummer), ustIdNr: s(k?.ust_id_nr), glaeubigerId: s(k?.glaeubiger_id),
  };
}

/** Status aus der Zeile (Spalte kontakte.status, Migration 2026-10-19); ohne Wert aktiv. */
export function startAktiv(k: SonstigerKontaktZeile | null): boolean {
  return (k?.status ?? 'aktiv').toLowerCase() !== 'inaktiv';
}

export type SpeicherErgebnis = { ok: true; id: string | null } | { ok: false; meldung: string | null };

export function useKontaktFormular(kontakt: SonstigerKontaktZeile | null) {
  const [felder, setFelder] = useState<KontaktFormFelder>(() => startFelder(kontakt));
  const [erweitert, setErweitert] = useState(true);
  const [aktiv, setAktiv] = useState(() => startAktiv(kontakt));
  const [fehler, setFehler] = useState<{ feld: KontaktPflichtfeld; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const setze = (feld: KontaktFeld, wert: string) => {
    setFelder(f => ({ ...f, [feld]: wert }));
    if (fehler && fehler.feld === feld) setFehler(null);
  };
  const setzeTab = (tab: KontaktTab) => { setFelder(f => ({ ...f, activeTab: tab })); setFehler(null); };

  /** Prüft Pflichtfelder (inline) und speichert: insert (Neu) bzw. update (Bearbeiten). */
  const speichern = async (): Promise<SpeicherErgebnis> => {
    const pflicht = pflichtfeldFehler(felder);
    setFehler(pflicht);
    if (pflicht) return { ok: false, meldung: null };
    setSaving(true);
    try {
      const payload = { ...baueKontaktPayload(felder), status: aktiv ? 'aktiv' : 'inaktiv' };
      if (kontakt) {
        const { error } = await supabase.from('kontakte').update(payload).eq('id', kontakt.id);
        if (error) throw error;
        return { ok: true, id: kontakt.id };
      }
      const { data, error } = await supabase.from('kontakte').insert(payload).select('id').single();
      if (error) throw error;
      return { ok: true, id: (data as { id: string } | null)?.id ?? null };
    } catch (err: unknown) {
      return { ok: false, meldung: 'Fehler: ' + ((err as { message?: string } | null)?.message || 'Unbekannter Fehler') };
    } finally {
      setSaving(false);
    }
  };

  return { felder, setze, setzeTab, erweitert, setErweitert, aktiv, setAktiv, fehler, saving, speichern };
}
